var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { IsArray, IsBoolean, IsEnum, IsISO8601, IsNumber, IsOptional, IsString, IsUUID, Min, ValidateNested } from 'class-validator';
import { Type } from 'class-transformer';
import { v4 as uuidv4 } from 'uuid';
import { format } from 'date-fns';
import { EggStatus } from '../enums';
export class Egg {
    constructor(data) {
        this.id = uuidv4();
        this.status = EggStatus.LAID;
        this.layDate = format(new Date(), 'yyyy-MM-dd');
        if (data) {
            const { layDate, incubationStartDate, hatchingDate, bandingDate, ...rest } = data;
            Object.assign(this, rest);
            // Handle date fields
            if (layDate) {
                this.layDate = typeof layDate === 'string' ? layDate : format(new Date(layDate), 'yyyy-MM-dd');
            }
            if (incubationStartDate) {
                this.incubationStartDate = typeof incubationStartDate === 'string' ? incubationStartDate : format(new Date(incubationStartDate), 'yyyy-MM-dd');
            }
            if (hatchingDate) {
                this.hatchingDate = typeof hatchingDate === 'string' ? hatchingDate : format(new Date(hatchingDate), 'yyyy-MM-dd');
            }
            if (bandingDate) {
                this.bandingDate = typeof bandingDate === 'string' ? bandingDate : format(new Date(bandingDate), 'yyyy-MM-dd');
            }
        }
    }
}
__decorate([
    IsUUID(),
    __metadata("design:type", String)
], Egg.prototype, "id", void 0);
__decorate([
    IsEnum(EggStatus),
    __metadata("design:type", String)
], Egg.prototype, "status", void 0);
__decorate([
    IsISO8601(),
    __metadata("design:type", String)
], Egg.prototype, "layDate", void 0);
__decorate([
    IsOptional(),
    IsISO8601(),
    __metadata("design:type", String)
], Egg.prototype, "incubationStartDate", void 0);
__decorate([
    IsOptional(),
    IsNumber(),
    Min(0),
    __metadata("design:type", Number)
], Egg.prototype, "incubationDays", void 0);
__decorate([
    IsOptional(),
    IsNumber(),
    Min(0),
    __metadata("design:type", Number)
], Egg.prototype, "bandingNotificationDays", void 0);
__decorate([
    IsOptional(),
    IsString(),
    __metadata("design:type", String)
], Egg.prototype, "comment", void 0);
__decorate([
    IsOptional(),
    IsISO8601(),
    __metadata("design:type", String)
], Egg.prototype, "hatchingDate", void 0);
__decorate([
    IsOptional(),
    IsISO8601(),
    __metadata("design:type", String)
], Egg.prototype, "bandingDate", void 0);
__decorate([
    IsOptional(),
    IsString(),
    __metadata("design:type", String)
], Egg.prototype, "bandNumber", void 0);
__decorate([
    IsOptional(),
    IsString(),
    __metadata("design:type", String)
], Egg.prototype, "failureReason", void 0);
__decorate([
    IsOptional(),
    IsString(),
    __metadata("design:type", String)
], Egg.prototype, "chickRearMethod", void 0);
export class Clutch {
    constructor(data) {
        this.id = uuidv4();
        this.date = format(new Date(), 'yyyy-MM-dd');
        this.eggs = [];
        if (data) {
            const { date, eggs, ...rest } = data;
            Object.assign(this, rest);
            // Handle date field
            if (date) {
                this.date = typeof date === 'string' ? date : format(new Date(date), 'yyyy-MM-dd');
            }
            // Handle eggs array
            if (eggs) {
                this.eggs = eggs.map(egg => new Egg(egg));
            }
        }
    }
}
__decorate([
    IsUUID(),
    __metadata("design:type", String)
], Clutch.prototype, "id", void 0);
__decorate([
    IsISO8601(),
    __metadata("design:type", String)
], Clutch.prototype, "date", void 0);
__decorate([
    ValidateNested({ each: true }),
    Type(() => Egg),
    __metadata("design:type", Array)
], Clutch.prototype, "eggs", void 0);
export class BreedingPair {
    constructor(data) {
        this.id = uuidv4();
        this.maleBirdId = '';
        this.femaleBirdId = '';
        this.pairingBeginDate = format(new Date(), 'yyyy-MM-dd');
        this.allowHybridization = false;
        this.cageNumber = '';
        this.descendantIds = [];
        this.clutches = [];
        if (data) {
            const { pairingBeginDate, pairingEndDate, clutches, ...rest } = data;
            Object.assign(this, rest);
            // Handle date fields
            if (pairingBeginDate) {
                this.pairingBeginDate = typeof pairingBeginDate === 'string' ? pairingBeginDate : format(new Date(pairingBeginDate), 'yyyy-MM-dd');
            }
            if (pairingEndDate) {
                this.pairingEndDate = typeof pairingEndDate === 'string' ? pairingEndDate : format(new Date(pairingEndDate), 'yyyy-MM-dd');
            }
            // Handle clutches array
            if (clutches) {
                this.clutches = clutches.map(clutch => new Clutch(clutch));
            }
        }
    }
    get currentClutch() {
        return this.clutches?.length ? this.clutches[this.clutches.length - 1] : undefined;
    }
}
__decorate([
    IsUUID(),
    __metadata("design:type", String)
], BreedingPair.prototype, "id", void 0);
__decorate([
    IsString(),
    __metadata("design:type", String)
], BreedingPair.prototype, "maleBirdId", void 0);
__decorate([
    IsString(),
    __metadata("design:type", String)
], BreedingPair.prototype, "femaleBirdId", void 0);
__decorate([
    IsISO8601(),
    __metadata("design:type", String)
], BreedingPair.prototype, "pairingBeginDate", void 0);
__decorate([
    IsOptional(),
    IsISO8601(),
    __metadata("design:type", String)
], BreedingPair.prototype, "pairingEndDate", void 0);
__decorate([
    IsOptional(),
    IsString(),
    __metadata("design:type", String)
], BreedingPair.prototype, "comments", void 0);
__decorate([
    IsOptional(),
    IsBoolean(),
    __metadata("design:type", Boolean)
], BreedingPair.prototype, "allowHybridization", void 0);
__decorate([
    IsOptional(),
    IsString(),
    __metadata("design:type", String)
], BreedingPair.prototype, "cageNumber", void 0);
__decorate([
    IsOptional(),
    IsArray(),
    IsString({ each: true }),
    __metadata("design:type", Array)
], BreedingPair.prototype, "descendantIds", void 0);
__decorate([
    ValidateNested({ each: true }),
    Type(() => Clutch),
    __metadata("design:type", Array)
], BreedingPair.prototype, "clutches", void 0);
